import { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Copy, Pencil, Users } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, hoyISO } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const CARD = {
  laborable: "border-amber-300/80 bg-gradient-to-br from-amber-50 to-yellow-50 text-amber-950 shadow-sm",
  vacaciones: "border-violet-400 bg-violet-300 text-violet-950 shadow-sm",
  ausencia: "border-orange-300 bg-orange-200 text-orange-950 shadow-sm",
  permiso_justificado: "border-orange-300/90 bg-gradient-to-br from-orange-100 to-amber-50 text-orange-950 shadow-sm ring-1 ring-orange-200/80",
  baja: "border-red-300 bg-red-200 text-red-950 shadow-sm",
  festivo: "border-sky-300 bg-sky-100 text-sky-950 shadow-sm",
  descanso: "border-zinc-200 bg-zinc-100/80 text-zinc-500",
};

function iniciales(nombre) {
  return (nombre || "?").split(" ").filter(Boolean).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

function lunesDe(iso) {
  const d = new Date(`${iso}T12:00:00`);
  const day = d.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  d.setDate(d.getDate() + diff);
  return d.toISOString().slice(0, 10);
}

function CeldaHorario({ celda, onClick, editable }) {
  if (!celda) {
    return <div className="h-[4.25rem] rounded-xl border border-dashed border-zinc-200 bg-white" />;
  }
  const cls = CARD[celda.codigo] || CARD.laborable;
  const puedeEditar = editable && celda.editable !== false && celda.codigo !== "vacaciones" && celda.codigo !== "baja" && celda.codigo !== "permiso_justificado" && celda.codigo !== "festivo";
  const inner = (
    <>
      <span className="line-clamp-2 text-[10px] font-semibold leading-tight">{celda.label}</span>
      {celda.detalle && celda.detalle !== celda.label && (celda.codigo === "laborable" || celda.codigo === "permiso_justificado") && (
        <span className="mt-0.5 line-clamp-2 text-[9px] font-normal opacity-80">{celda.detalle}</span>
      )}
      {celda.horas && (celda.codigo === "laborable" || celda.codigo === "permiso_justificado") && (
        <span className="text-[9px] font-medium opacity-60">{celda.horas}</span>
      )}
      {puedeEditar && (
        <Pencil className="absolute right-1 top-1 h-3 w-3 opacity-0 transition group-hover:opacity-40" />
      )}
    </>
  );
  const style = celda.color && (celda.codigo === "laborable" || celda.codigo === "permiso_justificado")
    ? { borderColor: celda.color, backgroundColor: `${celda.color}18` }
    : undefined;

  if (puedeEditar && onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        title={celda.detalle || celda.label}
        style={style}
        className={`group relative flex h-[4.25rem] w-full flex-col justify-center rounded-xl border px-1.5 py-1 text-left transition hover:ring-2 hover:ring-violet-300 ${cls}`}
      >
        {inner}
      </button>
    );
  }
  return (
    <div
      title={celda.detalle || celda.label}
      style={style}
      className={`relative flex h-[4.25rem] flex-col justify-center rounded-xl border px-1.5 py-1 ${cls}`}
    >
      {inner}
    </div>
  );
}

export default function HorariosSemanal({ departamentos = [], refreshKey = 0 }) {
  const { user } = useAuth();
  const esAdmin = user?.rol === "admin";
  const esManager = user?.rol === "manager";
  const puedeEditar = esAdmin || esManager;
  const [semanaInicio, setSemanaInicio] = useState(() => lunesDe(hoyISO()));
  const [depId, setDepId] = useState("all");
  const [empFiltro, setEmpFiltro] = useState("all");
  const [data, setData] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [copiando, setCopiando] = useState(false);
  const [dlg, setDlg] = useState(null);
  const [turnoSel, setTurnoSel] = useState("");
  const [alcance, setAlcance] = useState("dia");
  const [guardando, setGuardando] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const params = { desde: semanaInicio };
      if (depId !== "all") params.departamento_id = Number(depId);
      const { data: res } = await api.get("/horarios/semanal", { params });
      setData(res);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCargando(false);
    }
  }, [semanaInicio, depId]);

  useEffect(() => { cargar(); }, [cargar, refreshKey]);

  const navegar = (delta) => {
    const d = new Date(`${semanaInicio}T12:00:00`);
    d.setDate(d.getDate() + delta * 7);
    setSemanaInicio(d.toISOString().slice(0, 10));
  };

  const irHoy = () => setSemanaInicio(lunesDe(hoyISO()));

  const esHoySemana = useMemo(() => {
    if (!data) return false;
    const hoy = hoyISO();
    return hoy >= data.desde && hoy <= data.hasta;
  }, [data]);

  const empleadosVisibles = useMemo(() => {
    if (!data?.empleados) return [];
    if (empFiltro === "all") return data.empleados;
    return data.empleados.filter((e) => String(e.usuario_id) === empFiltro);
  }, [data, empFiltro]);

  const copiarSemana = async () => {
    if (!window.confirm("¿Copiar los turnos de la semana anterior a esta semana para los empleados del filtro actual?")) return;
    setCopiando(true);
    try {
      const payload = { desde: semanaInicio };
      if (depId !== "all") payload.departamento_id = Number(depId);
      const { data: res } = await api.post("/horarios/semanal/copiar-semana", payload);
      toast.success(`Semana copiada (${res.asignaciones} asignaciones)`);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCopiando(false);
    }
  };

  const abrirCelda = (emp, celda) => {
    if (!puedeEditar || !celda.editable) return;
    setDlg({ emp, celda });
    setTurnoSel(celda.turno_id ? String(celda.turno_id) : "");
    setAlcance("dia");
  };

  const guardarCelda = async () => {
    if (!dlg) return;
    setGuardando(true);
    try {
      const payload = {
        usuario_id: dlg.emp.usuario_id,
        fecha: dlg.celda.fecha,
      };
      if (alcance === "semana" && data) {
        payload.hasta = data.hasta;
      }
      if (turnoSel && turnoSel !== "ninguno") {
        payload.turno_id = Number(turnoSel);
      } else {
        payload.turno_id = null;
      }
      await api.post("/horarios/semanal/asignar", payload);
      toast.success(turnoSel && turnoSel !== "ninguno" ? "Turno asignado" : "Turno quitado (horario de departamento)");
      setDlg(null);
      cargar();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setGuardando(false);
    }
  };

  const turnosPlantilla = data?.turnos || [];

  return (
    <div className="space-y-4" data-testid="horarios-semanal">
      {/* Barra superior */}
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-zinc-200 bg-white px-4 py-3 shadow-sm">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg" onClick={() => navegar(-1)}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <div className="text-center">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">Semana</p>
            <p className="text-sm font-semibold text-zinc-900">
              {data ? `${fmtFecha(data.desde)} — ${fmtFecha(data.hasta)}` : "…"}
            </p>
          </div>
          <Button variant="outline" size="icon" className="h-9 w-9 rounded-lg" onClick={() => navegar(1)}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          {!esHoySemana && (
            <Button variant="outline" size="sm" className="rounded-lg" onClick={irHoy}>Hoy</Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <select
            className="h-9 rounded-lg border border-zinc-200 bg-zinc-50 px-2 text-sm"
            value={depId}
            onChange={(e) => { setDepId(e.target.value); setEmpFiltro("all"); }}
          >
            <option value="all">Todos los departamentos</option>
            {(data?.departamentos || departamentos).map((d) => (
              <option key={d.id} value={d.id}>{d.nombre}</option>
            ))}
          </select>
          <select
            className="h-9 min-w-[9rem] rounded-lg border border-zinc-200 bg-zinc-50 px-2 text-sm"
            value={empFiltro}
            onChange={(e) => setEmpFiltro(e.target.value)}
          >
            <option value="all">Todos los empleados</option>
            {(data?.empleados || []).map((e) => (
              <option key={e.usuario_id} value={e.usuario_id}>{e.nombre}</option>
            ))}
          </select>
          {puedeEditar && (
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5 rounded-lg"
              onClick={copiarSemana}
              disabled={copiando}
              data-testid="copiar-semana-btn"
            >
              <Copy className="h-3.5 w-3.5" />
              {copiando ? "Copiando…" : "Copiar semana ant."}
            </Button>
          )}
          {data?.total_horas_semana && (
            <Badge className="bg-zinc-900 text-white hover:bg-zinc-800">
              {data.total_horas_semana} planificadas
            </Badge>
          )}
        </div>
      </div>

      {/* Resumen turnos */}
      {data?.resumen_turnos && Object.keys(data.resumen_turnos).length > 0 && (
        <div className="flex flex-wrap gap-2">
          {Object.entries(data.resumen_turnos).map(([nombre, count]) => {
            const t = turnosPlantilla.find((x) => x.nombre === nombre);
            return (
              <Badge
                key={nombre}
                variant="outline"
                className="gap-1.5 border-zinc-300 py-1"
                style={t ? { borderColor: t.color, color: t.color } : undefined}
              >
                <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: t?.color || "#fbbf24" }} />
                {nombre}: {count}
              </Badge>
            );
          })}
        </div>
      )}

      {cargando && !data && (
        <p className="py-12 text-center text-sm text-zinc-400">Cargando planificación…</p>
      )}

      {data && (
        <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-[#fafaf9] shadow-sm">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] border-collapse text-sm">
              <thead>
                <tr className="border-b border-zinc-200 bg-white">
                  <th className="sticky left-0 z-20 min-w-[12rem] border-r border-zinc-200 bg-white px-3 py-2 text-left">
                    <span className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-zinc-500">
                      <Users className="h-3.5 w-3.5" /> Empleado
                    </span>
                  </th>
                  {data.dias.map((d) => {
                    const hoy = d.fecha === hoyISO();
                    const cov = data.cobertura[d.fecha];
                    return (
                      <th
                        key={d.fecha}
                        className={`min-w-[6.5rem] border-r border-zinc-100 px-1 py-2 text-center last:border-r-0 ${hoy ? "bg-zinc-900 text-white" : "bg-white text-zinc-600"}`}
                      >
                        <div className="text-[10px] font-medium uppercase">{d.nombre.slice(0, 3)}</div>
                        <div className="text-sm font-bold">{d.fecha.slice(8, 10)}</div>
                        {cov && (
                          <div className={`mt-0.5 text-[9px] font-normal ${hoy ? "text-zinc-300" : "text-zinc-400"}`}>
                            {cov.laborables} pers.
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
                {/* Fila cobertura / plantillas */}
                <tr className="border-b border-dashed border-zinc-200 bg-zinc-50/80">
                  <td className="sticky left-0 z-20 border-r border-zinc-200 bg-zinc-50 px-3 py-2 text-[10px] font-semibold uppercase text-zinc-500">
                    Cobertura
                  </td>
                  {data.dias.map((d) => {
                    const turnosDia = data.cobertura[d.fecha]?.turnos || {};
                    const entries = Object.entries(turnosDia);
                    return (
                      <td key={d.fecha} className="border-r border-zinc-100 p-1 align-top last:border-r-0">
                        <div className="flex min-h-[3rem] flex-col gap-1">
                          {entries.length === 0 ? (
                            <div className="flex h-12 items-center justify-center rounded-lg border border-dashed border-zinc-300 text-[9px] text-zinc-400">
                              —
                            </div>
                          ) : (
                            entries.map(([nombre, n]) => {
                              const t = turnosPlantilla.find((x) => x.nombre === nombre);
                              return (
                                <div
                                  key={nombre}
                                  className="rounded-lg border border-dashed px-1 py-0.5 text-center text-[9px] font-medium leading-tight"
                                  style={{
                                    borderColor: t?.color || "#d4d4d8",
                                    backgroundColor: `${t?.color || "#e4e4e7"}33`,
                                    color: "#3f3f46",
                                  }}
                                >
                                  {nombre}
                                  <span className="ml-0.5 rounded bg-white/80 px-1 font-bold">{n}</span>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </td>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {empleadosVisibles.map((emp) => (
                  <tr key={emp.usuario_id} className="border-b border-zinc-100 bg-white hover:bg-zinc-50/40">
                    <td className="sticky left-0 z-10 border-r border-zinc-100 bg-white px-3 py-2">
                      <div className="flex items-center gap-2.5">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-violet-600 text-xs font-bold text-white shadow-sm">
                          {iniciales(emp.nombre)}
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-zinc-900">{emp.nombre}</p>
                          <p className="truncate text-[10px] text-zinc-400">{emp.puesto || emp.departamento || "—"}</p>
                          <p className="text-[10px] font-semibold text-indigo-600">{emp.horas_semana}/sem</p>
                          {emp.horario_propio && (
                            <p className="text-[9px] font-medium text-amber-700" title="No hereda el horario del departamento">
                              Horario propio
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    {emp.dias.map((celda) => (
                      <td key={celda.fecha} className="border-r border-zinc-50 p-1.5 align-top last:border-r-0">
                        <CeldaHorario
                          celda={celda}
                          editable={puedeEditar}
                          onClick={() => abrirCelda(emp, celda)}
                        />
                      </td>
                    ))}
                  </tr>
                ))}
                {empleadosVisibles.length === 0 && (
                  <tr>
                    <td colSpan={8} className="p-10 text-center text-zinc-400">
                      Sin empleados con los filtros actuales.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <p className="text-xs text-zinc-500">
        {puedeEditar
          ? "Haz clic en una celda para asignar un turno plantilla. Quien tenga «horario propio» no hereda el del departamento hasta que lo quites o marques «Aplicar a empleados» al guardar el departamento."
          : "Vista de solo lectura. Contacta con el administrador para modificar turnos."}
      </p>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Asignar turno</DialogTitle>
          </DialogHeader>
          {dlg && (
            <div className="space-y-4 text-sm">
              <p className="rounded-lg bg-violet-50 px-3 py-2 text-violet-900">
                <strong>{dlg.emp.nombre}</strong>
                <br />
                <span className="text-violet-700">{fmtFecha(dlg.celda.fecha)} · {dlg.celda.dia_semana}</span>
              </p>
              <div className="space-y-1.5">
                <Label>Turno</Label>
                <Select value={turnoSel || "ninguno"} onValueChange={setTurnoSel}>
                  <SelectTrigger><SelectValue placeholder="Selecciona turno" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ninguno">Sin turno (horario departamento)</SelectItem>
                    {turnosPlantilla.map((t) => (
                      <SelectItem key={t.id} value={String(t.id)}>
                        <span className="inline-flex items-center gap-2">
                          <span className="h-2 w-2 rounded-full" style={{ backgroundColor: t.color }} />
                          {t.nombre}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Aplicar a</Label>
                <Select value={alcance} onValueChange={setAlcance}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dia">Solo este día</SelectItem>
                    <SelectItem value="semana">Resto de la semana</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(null)}>Cancelar</Button>
            <Button onClick={guardarCelda} disabled={guardando} className="bg-violet-600 hover:bg-violet-700">
              {guardando ? "Guardando…" : "Guardar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
